import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../store/hooks/reduxHooks';
import { UserRole } from '../types/auth';
import { User as UserIcon, Mail, Calendar, Shield, Clock, CheckCircle, Users } from 'lucide-react';
import CleverPeLogo from '/CleverPeLogos/CleverPe_White_H.svg';

const ProfilePage = () => {
  const { user } = useAppSelector(state => state.auth);
  const [activeTab, setActiveTab] = useState<'profile' | 'security'>('profile');
  const [logoLoaded, setLogoLoaded] = useState(false);
  
  // Preload the logo image
  useEffect(() => {
    const img = new Image();
    img.src = CleverPeLogo;
    img.onload = () => setLogoLoaded(true);
  }, []);

  if (!user) {
    return (
      <div className="p-6">
        <p className="text-red-500">User data not available</p>
      </div>
    );
  }

  // Format role name for display
  const formatRole = (role: string) => {
    return role.replace(/_/g, ' ').split(' ').map(
      word => word.charAt(0) + word.slice(1).toLowerCase()
    ).join(' ');
  };

  // Get role badge class
  const getRoleBadgeClass = () => {
    const baseClasses = "px-2 py-0.5 text-xs font-medium rounded-full";
    
    switch (user.role) {
      case UserRole.SUPERADMIN:
        return `${baseClasses} bg-purple-100 text-purple-800`;
      case UserRole.ADMIN:
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case UserRole.CLEVERPE_USER:
        return `${baseClasses} bg-green-100 text-green-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  return (
    <div className="p-5 bg-gray-50">
      {/* Main Card */}
      <div className="max-w-4xl mx-auto overflow-hidden bg-white rounded-lg shadow">
        {/* Header with gradient background */}
        <div className="relative">
          <div className="flex items-center justify-end h-20 pr-6 bg-gradient-to-r from-blue-500 to-indigo-600">
            {logoLoaded ? (
              <img 
                src={CleverPeLogo} 
                alt="CleverPe Logo" 
                className="h-10" 
                width="120" 
                height="40"
                loading="eager"
              />
            ) : (
              <div className="h-10 bg-blue-400 rounded w-28 animate-pulse"></div>
            )}
          </div>
          
          {/* Profile Avatar - positioned to overflow properly */}
          <div className="absolute bottom-0 transform translate-y-1/2 left-8">
            <div className="flex items-center justify-center w-20 h-20 text-3xl font-bold text-white border-4 border-white rounded-full shadow-md bg-gradient-to-r from-indigo-500 to-purple-600">
              {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </div>
          </div>
        </div>
        
        {/* User info area */}
        <div className="px-8 pb-4 pt-14">
          <h2 className="text-2xl font-bold text-gray-900">{user.name}</h2>
          <div className="flex items-center mt-1">
            <span className={getRoleBadgeClass()}>
              {formatRole(user.role)}
            </span>
            <div className="flex items-center ml-3">
              <span className="inline-block w-2 h-2 mr-1 bg-green-600 rounded-full"></span>
              <span className="text-xs text-gray-500">Active</span>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="px-8">
            <nav className="flex space-x-6">
              <button
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'profile'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('profile')}
              >
                Profile
              </button>
              <button
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'security'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('security')}
              >
                Security
              </button>
            </nav>
          </div>
        </div>
        
        {/* Content area */}
        <div className="p-8">
          {activeTab === 'profile' && (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Basic Information */}
              <div>
                <h3 className="mb-4 text-base font-semibold text-gray-900">Basic Information</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <UserIcon className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="ml-3">
                      <div className="text-xs font-medium text-gray-500">Full Name</div>
                      <div className="text-sm font-medium text-gray-900 mt-0.5">{user.name || 'Not provided'}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Mail className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="ml-3">
                      <div className="text-xs font-medium text-gray-500">Email Address</div>
                      <div className="text-sm font-medium text-gray-900 mt-0.5">{user.email || 'Not provided'}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Calendar className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="ml-3">
                      <div className="text-xs font-medium text-gray-500">Phone Number</div>
                      <div className="text-sm font-medium text-gray-900 mt-0.5">{user.mobileNo || 'Not provided'}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Account Information */}
              <div>
                <h3 className="mb-4 text-base font-semibold text-gray-900">Account Information</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Shield className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="ml-3">
                      <div className="text-xs font-medium text-gray-500">Role</div>
                      <div className="text-sm font-medium mt-0.5">
                        <span className={getRoleBadgeClass()}>
                          {formatRole(user.role)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <Clock className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="ml-3">
                      <div className="text-xs font-medium text-gray-500">Last Login</div>
                      <div className="text-sm font-medium text-gray-900 mt-0.5">{user.lastLogin || 'Not available'}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    </div>
                    <div className="ml-3">
                      <div className="text-xs font-medium text-gray-500">Status</div>
                      <div className="text-sm font-medium text-green-600 mt-0.5">
                        Active
                      </div>
                    </div>
                  </div>
                  
                  {/* Only show Groups for cleverpeuser role */}
                  {user.role === UserRole.CLEVERPE_USER && user.groups && user.groups.length > 0 && (
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <Users className="w-4 h-4 text-blue-500" />
                      </div>
                      <div className="ml-3">
                        <div className="text-xs font-medium text-gray-500">Group Category</div>
                        <div className="mt-0.5">
                          {user.groups.map((group, index) => (
                            <span key={index} className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full mr-1 mb-1">
                              {group}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div>
              <h3 className="mb-4 text-base font-semibold text-gray-900">Account Security</h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <Shield className="w-4 h-4 text-blue-500" />
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium text-gray-700">Security</div>
                    <div className="text-sm text-green-500 mt-0.5">
                      Your account is secured with phone number verification.
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium text-gray-700">
                      Account Status: {user.activated ? 'Activated' : 'Not Activated'}
                    </div>
                    {!user.activated && (
                      <div className="text-sm text-red-500 mt-0.5">
                        Please contact your administrator to activate your account.
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <Clock className="w-4 h-4 text-gray-500" />
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium text-gray-700">
                      Last Login: {user.lastLogin || 'Not available'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;